'use client';

import * as React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronDownIcon, ChevronUpIcon, UsersRoundIcon } from 'lucide-react';
import { motion } from 'motion/react';

import { APP_GITHUB_REPO_URL } from '@workspace/common/app';
import { baseUrl, getPathname, routes } from '@workspace/routes';
import { GitHubIcon } from '@workspace/ui/components/brand-icons';
import { Button, buttonVariants } from '@workspace/ui/components/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@workspace/ui/components/collapsible';
import { Logo } from '@workspace/ui/components/logo';
import { Portal } from '@workspace/ui/components/portal';
import { ThemeSwitcher } from '@workspace/ui/components/theme-switcher';
import { RemoveScroll } from '@workspace/ui/lib/remove-scroll';
import { cn } from '@workspace/ui/lib/utils';

import {
  COMMUNITY_LINKS,
  DOCS_LINKS,
  MENU_LINKS
} from '~/components/marketing-links';
import { useMarketingAnalytics } from '~/hooks/use-marketing-analytics';

export function MobileMenu({
  className,
  ...other
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  const [open, setOpen] = React.useState<boolean>(false);
  const pathname = usePathname();
  const isDocs = pathname.startsWith(
    getPathname(routes.marketing.Docs, baseUrl.Marketing)
  );

  React.useEffect(() => {
    const handleRouteChangeStart = () => {
      if (document.activeElement instanceof HTMLInputElement) {
        document.activeElement.blur();
      }

      setOpen(false);
    };

    handleRouteChangeStart();
  }, [pathname]);

  const handleChange = () => {
    const mediaQueryList = window.matchMedia('(min-width: 1024px)');
    setOpen((currentOpen) => (currentOpen ? !mediaQueryList.matches : false));
  };

  React.useEffect(() => {
    handleChange();
    const mediaQueryList = window.matchMedia('(min-width: 1024px)');
    mediaQueryList.addEventListener('change', handleChange);
    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, []);

  const handleToggleMobileMenu = (): void => {
    setOpen((currentOpen) => !currentOpen);
  };

  return (
    <>
      <div
        className={cn('flex items-center justify-between', className)}
        {...other}
      >
        <Link href={routes.marketing.Index}>
          <Logo />
        </Link>
        <Button
          variant="ghost"
          size="icon"
          aria-expanded={open}
          aria-label="Toggle Mobile Menu"
          onClick={handleToggleMobileMenu}
          className="flex aspect-square h-fit select-none flex-col items-center justify-center rounded-full"
        >
          <motion.div
            className="w-5 origin-center border-t-2 border-primary"
            animate={
              open ? { rotate: '45deg', y: '5px' } : { rotate: '0deg', y: 0 }
            }
            transition={{ bounce: 0, duration: 0.1 }}
          />
          <motion.div
            className="w-5 origin-center border-t-2 border-primary"
            transition={{ bounce: 0, duration: 0.1 }}
            animate={
              open
                ? { rotate: '-45deg', y: '-5px' }
                : { rotate: '0deg', scaleX: 1, y: 0 }
            }
          />
        </Button>
      </div>

      {open && (
        <Portal asChild>
          <RemoveScroll
            allowPinchZoom
            enabled
          >
            {isDocs ? (
              <DocsMobileMenu onLinkClicked={handleToggleMobileMenu} />
            ) : (
              <MainMobileMenu onLinkClicked={handleToggleMobileMenu} />
            )}
          </RemoveScroll>
        </Portal>
      )}
    </>
  );
}

type MainMobileMenuProps = {
  onLinkClicked: () => void;
};

function MainMobileMenu({
  onLinkClicked
}: MainMobileMenuProps): React.JSX.Element {
  const [communityOpen, setCommunityOpen] = React.useState<boolean>(false);
  const { trackDiscordClick, trackDocsClick, trackGithubClick } =
    useMarketingAnalytics();
  const handleCommunityClick = (name: string): void => {
    if (name === 'Discord') {
      trackDiscordClick('mobile_menu_community_discord');
    }

    if (name === 'GitHub') {
      trackGithubClick('mobile_menu_community_github');
    }

    onLinkClicked();
  };

  return (
    <div className="fixed inset-0 z-50 mt-[69px] overflow-y-auto bg-background animate-in fade-in-0">
      <div className="flex size-full flex-col items-start space-y-6 p-4">
        <div className="flex w-full flex-col gap-2">
          <Link
            href={routes.marketing.Contact}
            className={cn(
              buttonVariants({
                variant: 'outline',
                size: 'lg'
              }),
              'w-full rounded-full'
            )}
            onClick={onLinkClicked}
          >
            Contact
          </Link>
          <Collapsible
            open={communityOpen}
            onOpenChange={setCommunityOpen}
            className="w-full"
          >
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="default"
                size="lg"
                className="h-11 w-full justify-center gap-2 rounded-full bg-[linear-gradient(135deg,#0f172a,#0e7490)] text-white shadow-[0_12px_34px_rgba(14,116,144,0.18)] dark:bg-[linear-gradient(135deg,#ffffff,#dff6ff)] dark:text-slate-950"
              >
                <UsersRoundIcon className="size-4" />
                Join our Community
                {communityOpen ? (
                  <ChevronUpIcon className="size-4" />
                ) : (
                  <ChevronDownIcon className="size-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 grid gap-2 rounded-2xl border border-border/70 bg-muted/20 p-2">
                {COMMUNITY_LINKS.map((item) =>
                  item.href ? (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleCommunityClick(item.name)}
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-background"
                    >
                      <span className="flex size-8 items-center justify-center rounded-full bg-background text-foreground">
                        {item.icon}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {item.handle}
                        </span>
                      </span>
                    </a>
                  ) : (
                    <div
                      key={item.name}
                      aria-disabled="true"
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left opacity-60"
                    >
                      <span className="flex size-8 items-center justify-center rounded-full bg-background text-muted-foreground">
                        {item.icon}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {item.handle}
                        </span>
                      </span>
                    </div>
                  )
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
          <a
            href={APP_GITHUB_REPO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackGithubClick('mobile_menu_github');
              onLinkClicked();
            }}
            className={cn(
              buttonVariants({
                variant: 'default',
                size: 'lg'
              }),
              'w-full rounded-full gap-2'
            )}
          >
            <GitHubIcon className="size-4" />
            GitHub
          </a>
        </div>

        <nav
          className="w-full"
          aria-label="Primary"
        >
          <ul className="flex w-full flex-col gap-2">
            {MENU_LINKS.map((item) => (
              <li key={item.title}>
                {item.external ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onLinkClicked}
                    className={cn(
                      buttonVariants({ variant: 'ghost' }),
                      'w-full justify-start rounded-xl text-base'
                    )}
                  >
                    {item.title}
                  </a>
                ) : (
                  <Link
                    href={item.href}
                    onClick={() => {
                      if (item.title === 'Docs') {
                        trackDocsClick('mobile_menu_docs', item.href);
                      }

                      onLinkClicked();
                    }}
                    className={cn(
                      buttonVariants({ variant: 'ghost' }),
                      'w-full justify-start rounded-xl text-base'
                    )}
                  >
                    {item.title}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex w-full items-center justify-between gap-2 border-y border-border/40 py-4">
          <div className="text-base font-medium">Theme</div>
          <ThemeSwitcher />
        </div>
      </div>
    </div>
  );
}

type DocsMobileMenuProps = {
  onLinkClicked: () => void;
};

function DocsMobileMenu({
  onLinkClicked
}: DocsMobileMenuProps): React.JSX.Element {
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const pathname = usePathname();

  return (
    <div className="fixed inset-0 z-50 mt-[69px] overflow-y-auto bg-background animate-in fade-in-0">
      <div className="flex size-full flex-col items-start space-y-3 p-4">
        <ul className="w-full">
          {DOCS_LINKS.map((item) => (
            <li
              key={item.title}
              className="py-2"
            >
              <Collapsible
                open={expanded[item.title.toLowerCase()]}
                onOpenChange={(isOpen) =>
                  setExpanded((prev) => ({
                    ...prev,
                    [item.title.toLowerCase()]: isOpen
                  }))
                }
              >
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="flex h-9 w-full items-center justify-between px-4 text-left"
                  >
                    <div className="flex flex-row items-center gap-2 text-base font-medium">
                      {item.icon}
                      {item.title}
                    </div>
                    {expanded[item.title.toLowerCase()] ? (
                      <ChevronUpIcon className="size-4" />
                    ) : (
                      <ChevronDownIcon className="size-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <ul className="mt-2 pl-4">
                    {item.items.map((subItem) => (
                      <li key={subItem.title}>
                        <Link
                          href={subItem.href}
                          className={cn(
                            buttonVariants({ variant: 'ghost' }),
                            'm-0 h-auto w-full justify-start gap-4 p-1.5 text-sm font-medium',
                            pathname ===
                              getPathname(subItem.href, baseUrl.Marketing)
                              ? 'bg-accent font-medium text-foreground'
                              : 'text-muted-foreground'
                          )}
                          onClick={onLinkClicked}
                        >
                          {subItem.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CollapsibleContent>
              </Collapsible>
            </li>
          ))}
        </ul>

        <div className="flex w-full items-center justify-between gap-2 border-y border-border/40 p-4">
          <div className="text-base font-medium">Theme</div>
          <ThemeSwitcher />
        </div>
      </div>
    </div>
  );
}
